import DropFiles from "e.sic-platform.webclient/src/components/upload-drop-files/DropFiles";
import UploadTable from "e.sic-platform.webclient/src/components/upload-drop-files/UploadTable";
import { uploadFilesService } from "e.sic-platform.webclient/src/services/upload/UploadFilesService";
import { observer } from "mobx-react-lite";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Grid, Stack } from "@mui/material";
import UploadStatus from "e.sic-platform.webclient/src/components/upload-status/UploadStatus";
import { FileUploadTracker } from "e.sic-platform.webclient/src/services/upload/FileUploadTracker";
import SmartApp, { useSmartApps } from "../../components/apps/SmartApp";
import SmartAppStep from "../../components/apps/SmartAppStep";
import RunVideoConversionButton from "../../components/buttons/run-video-conversion-button/RunVideoConversionButton";
import PurchaseConfirmation from "../../components/apps/PurchaseConfirmation";
import { ActivityTypes } from "e.sic-platform.webclient/src/services/shop/ActivityPricesStorage";
import { creditBalanceStorage } from "e.sic-platform.webclient/src/services/shop/CreditBalanceStorage";
import useUrlQueryParams from "e.sic-platform.webclient/src/services/url/UrlQueryService";
import CopyUrlButton from "../../components/buttons/copy-url-button/CopyUrlButton";
import {
  VideoConversionModel,
  VideoConversionService,
} from "e.sic-platform.webclient/src/services/conversion/VideoConversionService";
import VideoConversionStatus from "e.sic-platform.webclient/src/services/conversion/VideoConversionStatus";
import { api } from "e.sic-platform.webclient/src/services/api/ApiService";
import LoadingSpinner from "e.sic-platform.webclient/src/components/loading-spinner/LoadingSpinner";

export default observer(function VideoConversionPage() {
  return (
    <SmartApp
      titleKey="general.apps.video-conversion.title"
      descriptionKey="videoConversion.description"
      image="/images/video-conversion.png"
    >
      <Page />
    </SmartApp>
  );
});

const Page = observer(function VideoConversionPage() {
  useEffect(() => {
    uploadFilesService.clear();
  }, [])

  uploadFilesService.allowOnlySingleFile(true);
  const showUploadTable = useMemo(
    () => uploadFilesService.filesForUpload.length !== 0,
    [uploadFilesService.filesForUpload.length]
  );
  const { id }: any = useUrlQueryParams();
  const { setStep, confirmationPending, setConfirmationPending } =
    useSmartApps();
  const [videoConversion] = useState<VideoConversionService>(
    VideoConversionModel.create({ fileId: id }, { api: api })
  );

  const canConvert = useMemo(
    () => uploadFilesService.filesForUpload.length !== 0,
    [uploadFilesService.filesForUpload.length]
  );

  const onConversionFinished = useCallback(
    async (fileId: number) => {
      if (fileId == videoConversion.fileId) {
        videoConversion.reload();
        await creditBalanceStorage.load();
      }
    },
    [videoConversion, creditBalanceStorage]
  );

  const onUploadFinished = useCallback((file: FileUploadTracker) => {
    file.completed();
    videoConversion.setFileId(file.id!);
    videoConversion.reload();
  }, [videoConversion]);

  const upload = useCallback(async () => {
    await uploadFilesService.startStandaloneUpload(
      onConversionFinished,
      onUploadFinished
    );
  }, [
    uploadFilesService.startStandaloneUpload,
    onConversionFinished,
    onUploadFinished,
  ]);

  if (videoConversion) {
    if (videoConversion.fileId) setStep(2);
    if (videoConversion.isConverted) setStep(3);
  }

  if (videoConversion && videoConversion.isLoading) return <Grid item xs={12}><LoadingSpinner /></Grid>

  return (
    <>
      <SmartAppStep
        step={1}
        titleKey="videoConversion.upload.title"
        descriptionKey="videoConversion.upload.description"
        visible={videoConversion.fileId === undefined}
      >
        <Grid item xs={12} sm={12}>
          <DropFiles
            singleFile
            onDroppedFiles={uploadFilesService.addFilesForUpload}
          />
        </Grid>
        <Grid item xs={12} sm={12} className="centered">
          {showUploadTable && (
            <UploadTable
              files={uploadFilesService.filesForUpload}
              onRemoveFile={uploadFilesService.removeFile}
            />
          )}
        </Grid>
        <Grid item xs={12} sm={12} className="centered">
          <Stack flexDirection="row">
            <RunVideoConversionButton
              disabled={!canConvert}
              onClick={() => setConfirmationPending(!confirmationPending)}
            />
            <PurchaseConfirmation
              app={ActivityTypes.VideoConversion}
              visible={confirmationPending}
              confirm={upload}
              toggle={() => setConfirmationPending(!confirmationPending)}
            />
          </Stack>
        </Grid>
        <Grid item xs={12} sm={12}>
          {uploadFilesService.uploadProgressModalIsVisbile && <UploadStatus />}
        </Grid>
      </SmartAppStep>
      <SmartAppStep
        step={2}
        titleKey="videoConversion.conversion.title"
        descriptionKey="videoConversion.conversion.description"
        visible={videoConversion.fileId !== undefined}
      >
        <Grid item xs={12} sm={12}>
          <VideoConversionStatus
            files={[
              {
                id: 1,
                filename: videoConversion?.name!,
                sas: videoConversion?.sas!,
                status: videoConversion?.isConverted!,
              },
            ]}
            onConversionFinished={() => videoConversion?.reload()}
          />
        </Grid>
      </SmartAppStep>
      <SmartAppStep
        titleKey="videoConversion.converted.title"
        descriptionKey="videoConversion.converted.description"
        step={3}
      />
      <Grid item xs={12}>
        {videoConversion.fileId !== undefined && <CopyUrlButton id={videoConversion.fileId} />}
      </Grid>
    </>
  );
});
